import { useState } from "react";
import apiClient from "../../services/api";
import { toast } from "sonner";
import { Plus, Trash2, Wrench, MoreHorizontal, Receipt } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
const ListCard = ({ title, icon, items, field, newValue, setNewValue, placeholder, testPrefix, onAdd, onRemove }) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-lg flex items-center gap-2">
        {icon}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex gap-2">
        <Input
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
          data-testid={`new-${testPrefix}-input`}
        />
        <Button onClick={onAdd} data-testid={`add-${testPrefix}-btn`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {items.map((item) => (
          <div key={item} className="flex items-center justify-between p-2 bg-muted/30 rounded">
            <span>{item}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onRemove(item)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        {items.length === 0 && <p className="text-muted-foreground text-center py-4">None defined</p>}
      </div>
    </CardContent>
  </Card>
);

const BidItemOptionsTab = ({
  materialUnits,
  materialCategories,
  equipmentUnits,
  equipmentCategories,
  miscUnits = [],
  billingCategories = [],
  getAuthHeaders,
  onRefresh,
}) => {
  const [newUnit, setNewUnit] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newEquipmentUnit, setNewEquipmentUnit] = useState("");
  const [newEquipmentCategory, setNewEquipmentCategory] = useState("");
  const [newMiscUnit, setNewMiscUnit] = useState("");
  const [newBillingCategory, setNewBillingCategory] = useState("");

  const addItem = async (field, list, value, setter) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    if (list.includes(trimmed)) { toast.error("Already exists"); return; }
    try {
      await apiClient.put(`/settings/options`, { [field]: [...list, trimmed] });
      onRefresh();
      setter("");
      toast.success("Added");
    } catch { toast.error("Failed to add"); }
  };

  const removeItem = async (field, list, value) => {
    try {
      await apiClient.put(`/settings/options`, { [field]: list.filter(i => i !== value) });
      onRefresh();
      toast.success("Removed");
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="bid-item-options-tab">
      <ListCard title="Material Units" items={materialUnits} field="material_units" newValue={newUnit} setNewValue={setNewUnit} placeholder="Add new unit (e.g., gallon)" testPrefix="unit" onAdd={() => addItem("material_units", materialUnits, newUnit, setNewUnit)} onRemove={(item) => removeItem("material_units", materialUnits, item)} />
      <ListCard title="Material Categories" items={materialCategories} field="material_categories" newValue={newCategory} setNewValue={setNewCategory} placeholder="Add new category (e.g., Tools)" testPrefix="category" onAdd={() => addItem("material_categories", materialCategories, newCategory, setNewCategory)} onRemove={(item) => removeItem("material_categories", materialCategories, item)} />
      <ListCard title="Equipment Units" icon={<Wrench className="w-4 h-4" />} items={equipmentUnits} field="equipment_units" newValue={newEquipmentUnit} setNewValue={setNewEquipmentUnit} placeholder="Add new unit (e.g., day)" testPrefix="equipment-unit" onAdd={() => addItem("equipment_units", equipmentUnits, newEquipmentUnit, setNewEquipmentUnit)} onRemove={(item) => removeItem("equipment_units", equipmentUnits, item)} />
      <ListCard title="Equipment Categories" icon={<Wrench className="w-4 h-4" />} items={equipmentCategories} field="equipment_categories" newValue={newEquipmentCategory} setNewValue={setNewEquipmentCategory} placeholder="Add new category (e.g., Scaffold)" testPrefix="equipment-category" onAdd={() => addItem("equipment_categories", equipmentCategories, newEquipmentCategory, setNewEquipmentCategory)} onRemove={(item) => removeItem("equipment_categories", equipmentCategories, item)} />
      <ListCard title="Misc Bid Items Units" icon={<MoreHorizontal className="w-4 h-4" />} items={miscUnits} field="misc_units" newValue={newMiscUnit} setNewValue={setNewMiscUnit} placeholder="Add new unit (e.g., trip, load)" testPrefix="misc-unit" onAdd={() => addItem("misc_units", miscUnits, newMiscUnit, setNewMiscUnit)} onRemove={(item) => removeItem("misc_units", miscUnits, item)} />
      <ListCard title="Billing Categories" icon={<Receipt className="w-4 h-4" />} items={billingCategories} field="billing_categories" newValue={newBillingCategory} setNewValue={setNewBillingCategory} placeholder="Add billing category (e.g., CMU, Brick Veneer)" testPrefix="billing-category" onAdd={() => addItem("billing_categories", billingCategories, newBillingCategory, setNewBillingCategory)} onRemove={(item) => removeItem("billing_categories", billingCategories, item)} />
    </div>
  );
};

export default BidItemOptionsTab;
